import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { 
  getTeacherList, 
  getTeacherDetail, 
  updateTeacher,
  getTeacherSchedule,
  getTeacherBookings
} from '@/api/teacher'
import { imageCacheUtil } from '@/utils/image-cache'

export const useTeacherStore = defineStore('teacher', () => {
  // State
  const teachers = ref([])
  const currentTeacher = ref(null)
  const loading = ref(false)
  const total = ref(0)
  
  // Filters
  const filters = ref({
    name: '',
    specialty: '',
    status: ''
  })

  // Getters
  const activeTeachers = computed(() => 
    teachers.value.filter(t => t.status === 1)
  )
  
  const inactiveTeachers = computed(() => 
    teachers.value.filter(t => t.status === 0)
  )
  
  const blockedTeachers = computed(() => 
    teachers.value.filter(t => t.status === 0)
  )
  
  const teachersCount = computed(() => teachers.value.length)
  
  const availableTeachers = computed(() => 
    teachers.value.filter(t => t.status === 1 && !t.isBusy)
  )

  // Actions
  const fetchTeachers = async (filterOptions = {}, forceRefresh = false) => {
    loading.value = true
    try {
      const params = {
        page: filterOptions.page || 1,
        pageSize: filterOptions.pageSize || 10,
        ...filterOptions
      }
      
      const res = await getTeacherList(params)
      
      if (res.success) {
        // Handle both array and object with list property
        if (Array.isArray(res.data)) {
          teachers.value = res.data
          total.value = res.data.length
        } else {
          teachers.value = res.data?.list || []
          total.value = res.data?.total || 0
        }
        
        // Warm cache with all teacher avatars (force overwrite if refreshing)
        const avatarUrls = teachers.value.map(t => t.avatar).filter(Boolean)
        imageCacheUtil.warmCache(avatarUrls, forceRefresh)
        
        if (Object.keys(filterOptions).length > 0) {
          filters.value = { ...filters.value, ...filterOptions }
        }
        
        return { success: true, data: teachers.value }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Teacher Store] Fetch error:', error)
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const fetchTeacherById = async (id) => {
    loading.value = true
    try {
      const res = await getTeacherDetail(id)
      
      if (res.success) {
        currentTeacher.value = res.data
        return { success: true, data: res.data }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Teacher Store] Fetch detail error:', error)
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const modifyTeacher = async (id, teacherData) => {
    try {
      const res = await updateTeacher(id, teacherData)
      
      if (res.success) {
        // Update in local list
        const index = teachers.value.findIndex(t => t.id === id)
        if (index !== -1) {
          teachers.value[index] = { ...teachers.value[index], ...teacherData }
        }
        
        // Update current if matches
        if (currentTeacher.value?.id === id) {
          currentTeacher.value = { ...currentTeacher.value, ...teacherData }
        }
        
        return { success: true, data: res.data }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Teacher Store] Update error:', error)
      return { success: false, error: error.message }
    }
  }

  const fetchTeacherSchedule = async (id, startDate, endDate) => {
    loading.value = true
    try {
      const res = await getTeacherSchedule(id, { startDate, endDate })
      
      if (res.success) {
        return { success: true, data: res.data }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Teacher Store] Schedule error:', error)
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const fetchTeacherBookingsAction = async (id, startDate, endDate) => {
    loading.value = true
    try {
      const res = await getTeacherBookings(id, { startDate, endDate })
      
      if (res.success) {
        return { success: true, data: res.data }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Teacher Store] Bookings error:', error)
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const clearCurrentTeacher = () => {
    currentTeacher.value = null
  }

  const resetFilters = () => {
    filters.value = {
      name: '',
      specialty: '',
      status: ''
    }
  }

  return {
    // State
    teachers,
    currentTeacher,
    loading,
    total,
    filters,
    
    // Getters
    activeTeachers,
    inactiveTeachers,
    blockedTeachers,
    teachersCount,
    availableTeachers,
    
    // Actions
    fetchTeachers,
    fetchTeacherById,
    modifyTeacher,
    fetchTeacherSchedule,
    fetchTeacherBookingsAction,
    clearCurrentTeacher,
    resetFilters
  }
})